import { useEffect, useRef, useState } from "react";
import {
	ArrowUpRight,
	Link,
	Video,
	ShieldCheck,
	Wallet,
	Users,
	ArrowRight,
	AudioLines,
} from "lucide-react";
import { request, signIn, type Session } from "./api";
import { Call } from "./Call";
import { SESSION_KEY, SESSION_REJECTED, clearSession, readSession, saveSession, readName, saveName } from "./session";

// ponytail: the invite id is fixed at load; an invited guest never creates a room.
const inviteFromUrl = location.pathname.startsWith("/m/")
	? location.pathname.slice(3)
	: "";

export function App() {
	const [session, setSession] = useState<Session | null>(readSession);
	const [companies, setCompanies] = useState<
		{ address: string; name: string }[]
	>([]);
	const [company, setCompany] = useState("");
	const [title, setTitle] = useState("");
	const [invite, setInvite] = useState(inviteFromUrl);
	const [name, setName] = useState(() => readName(session?.address));
	const [call, setCall] = useState<string | null>(null);
	const restoring = useRef(!!session);
	const sessionRef = useRef(session);
	sessionRef.current = session;
	const expiredInCall = useRef(false);
	const [error, setError] = useState("");
	const [busy, setBusy] = useState(false);
	const [preview, setPreview] = useState<MediaStream | null>(null);
	const video = useRef<HTMLVideoElement>(null);
	function disconnect() {
		sessionRef.current = null;
		clearSession();
		setCall(null);
		setSession(null);
		setName("");
		setCompanies([]);
		setCompany("");
		expiredInCall.current = false;
	}
	useEffect(() => {
		if (!session) return;
		let disposed = false;
		const rejected = () => {
			if (call) expiredInCall.current = true; // retain established peer media until Leave
			else {
				disconnect();
				setError("Your wallet session expired. Connect your wallet again.");
			}
		};
		const switched = (accounts: string[]) => {
			if (!accounts.length || accounts[0].toLowerCase() !== session.address.toLowerCase()) disconnect();
		};
		const storage = (event: StorageEvent) => {
			if (event.key === SESSION_KEY && readSession()?.token !== session.token) disconnect();
		};
		window.addEventListener(SESSION_REJECTED, rejected);
		window.addEventListener("storage", storage);
		const ethereum = window.ethereum;
		ethereum?.on?.("accountsChanged", switched);
		void ethereum?.request({ method: "eth_accounts" }).then(accounts => {
			if (!disposed && Array.isArray(accounts) && accounts.length) switched(accounts);
		}).catch(() => {});
		return () => {
			disposed = true;
			window.removeEventListener(SESSION_REJECTED, rejected);
			window.removeEventListener("storage", storage);
			ethereum?.removeListener?.("accountsChanged", switched);
		};
	}, [session, call]);
	useEffect(() => {
		if (!session) return;
		let disposed = false;
		// Profile is a name default; it must never overwrite a name the person typed.
		void request<{ displayName?: string; username?: string; alias?: string }>("/api/v1/account", session)
			.then(profile => {
				if (disposed) return;
				const suggested = [profile.displayName, profile.username, profile.alias].find(value => typeof value === "string" && value.trim());
				if (suggested) setName(current => {
					if (current) return current;
					saveName(session.address, suggested.slice(0, 60));
					return suggested.slice(0, 60);
				});
			}).catch(() => {});
		if (!inviteFromUrl) void loadCompanies(session).catch(e => {
			if (!disposed) setError(e instanceof Error ? e.message : "Could not load companies");
		});
		else if (restoring.current) {
			restoring.current = false;
			// Validate a remembered invite session before opening the call. A revoked
			// token must return to login, rather than strand a person in a failed call.
			void request("/api/v1/meetings/companies", session).then(() => {
				if (!disposed) join(inviteFromUrl);
			}).catch(e => {
				if (!disposed) setError(e instanceof Error ? e.message : "Could not restore meeting");
			});
		}
		return () => { disposed = true; };
	}, [session]);
	useEffect(() => {
		if (video.current) video.current.srcObject = preview;
		return () => preview?.getTracks().forEach((t) => t.stop());
	}, [preview]);
	async function run(fn: () => Promise<void>) {
		setBusy(true);
		setError("");
		try {
			await fn();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Something went wrong");
		} finally {
			setBusy(false);
		}
	}
	async function login() {
		const s = await signIn();
		restoring.current = false;
		saveSession(s);
		setName(readName(s.address));
		setSession(s);
		expiredInCall.current = false;
		if (inviteFromUrl) {
			join(inviteFromUrl);
			return;
		}
	}
	async function loadCompanies(s: Session) {
		const result = await request<{
			companies: { address: string; name: string }[];
		}>("/api/v1/meetings/companies", s);
		if (sessionRef.current?.token !== s.token) return;
		setCompanies(result.companies);
		setCompany(result.companies[0]?.address || "");
	}
	function join(id: string) {
		let value = id.trim();
		try {
			if (value.includes("://"))
				value = new URL(value).pathname.replace(/^\/m\//, "");
		} catch {}
		if (!/^[a-zA-Z0-9_-]{1,80}$/.test(value))
			throw new Error("Paste a valid meeting link or ID.");
		preview?.getTracks().forEach((t) => t.stop());
		setPreview(null);
		setCall(value);
		history.replaceState(null, "", `/m/${value}`);
	}
	if (call && session)
		return (
			<Call
				id={call}
				session={session}
				name={name.trim() || session.address.slice(0, 10)}
				onLeave={() => {
					setCall(null);
					if (expiredInCall.current) disconnect();
					history.replaceState(null, "", "/");
				}}
			/>
		);
	return (
		<div className="landing">
			<header className="site-header">
				<a href="/" className="brand">
					<span className="brand-mark">
						<AudioLines size={22} />
					</span>
					Pioneer <span className="brand-light">Meet</span>
				</a>
				<div className="header-right">
					<span className="status-dot" />
					Small rooms. Real connection.
					<a
						className="office-link"
						href="https://office.pioneers.dev"
						target="_blank"
						rel="noreferrer"
					>
						Pioneer Office <ArrowUpRight size={15} />
					</a>
				</div>
			</header>
			<main className="welcome-grid">
				<section className="welcome-copy">
					<div className="eyebrow">
						<span />A PLACE TO THINK TOGETHER
					</div>
					<h1>
						Good work starts
						<br />
						with a <em>conversation.</em>
					</h1>
					<p className="intro">
						Bring your people into the room. Video, screen sharing, and
						chat—with your company’s memory back in Office.
					</p>
					<div className="join-card">
						<div className="card-title">
							<h2>
								{invite ? "Your room is waiting" : "Make room for an idea"}
							</h2>
							<span className="capacity">
								<Users size={14} /> Up to 6
							</span>
						</div>
						{!session ? (
							<>
								<p>
									Sign in with your Ethereum wallet to start or join a meeting.
									No password. No company membership needed to join.
								</p>
								<button
									className="primary wide"
									disabled={busy}
									onClick={() => void run(login)}
								>
									<Wallet size={18} />
									{busy ? "Waiting for wallet…" : "Connect wallet"}
									<ArrowRight size={18} />
								</button>
								{!inviteFromUrl && (
									<button className="primary wide" disabled={busy} onClick={() => void run(login)}><Video size={18} />Start a meeting</button>
								)}
							</>
						) : (
							<>
								<div className="identity">
									<ShieldCheck size={16} />
									<span>
										{session.address.slice(0, 6)}…{session.address.slice(-4)}
									</span>
									<button
										className="text-button"
										onClick={disconnect}
									>
										Disconnect
									</button>
								</div>
								<label>
									Your name
									<input
										value={name}
										onChange={(e) => {
											setName(e.target.value);
											saveName(session.address, e.target.value);
										}}
										maxLength={60}
										placeholder="How should people know you?"
									/>
								</label>
								{!inviteFromUrl && (
									<div className="create-fields">
										{companies.length === 0 && <p>No Office company is linked to this wallet.</p>}
										<p>{company ? "Office can save recordings and transcripts for your company." : "Anyone with your link and a wallet can join. No recording or transcripts. One-hour limit."}</p>
										<button className="text-button" disabled={busy} onClick={() => void run(() => loadCompanies(session))}>Refresh companies</button>
										<label>
											Company
											<select
												value={company}
												onChange={(e) => setCompany(e.target.value)}
											>
												<option value="">No Office — no recording or transcripts</option>
												{companies.map((c) => (
													<option key={c.address} value={c.address}>
														{c.name}
													</option>
												))}
											</select>
										</label>
										<label>
											Meeting name
											<input
												placeholder="Team catch-up"
												maxLength={120}
												value={title}
												onChange={(e) => setTitle(e.target.value)}
											/>
										</label>
										<button
											className="primary wide"
											disabled={busy}
											onClick={() =>
												void run(async () => {
													const r = await request<{ id: string }>(
														"/api/v1/meetings",
														session,
														{ company, title },
													);
													join(r.id);
												})
											}
										>
											<Video size={17} />
											Start a meeting
										</button>
									</div>
								)}
								<div className="join-link">
									<input
										aria-label="Meeting link or ID"
										placeholder="Paste a meeting link or ID"
										value={invite}
										onChange={(e) => setInvite(e.target.value)}
									/>
									<button
										disabled={busy || !invite.trim()}
										onClick={() => void run(async () => join(invite))}
									>
										Join <ArrowRight size={16} />
									</button>
								</div>
							</>
						)}
						{error && (
							<p role="alert" className="error">
								{error}
							</p>
						)}
					</div>
					<div className="trust-line">
						<ShieldCheck size={15} /> Wallet verified <span>·</span> Company
						owned <span>·</span> Peer to peer
					</div>
				</section>
				<section className="preview-section">
					<div className="preview-label">
						<span className="status-dot" /> YOUR SPACE, BEFORE YOU JOIN{" "}
						<span>01 / 06</span>
					</div>
					<div className="preview-stage">
						{preview ? (
							<video ref={video} autoPlay playsInline muted />
						) : (
							<>
								<div className="orbit orbit-one" />
								<div className="orbit orbit-two" />
								<div className="preview-avatar">
									<AudioLines size={47} />
								</div>
								<div className="preview-caption">
									<h3>A little closer, wherever you are.</h3>
									<p>Check your camera. Find your people.</p>
								</div>
							</>
						)}
						<button
							className="preview-camera"
							onClick={() =>
								void run(async () => {
									if (preview) {
										setPreview(null);
										return;
									}
									setPreview(
										await navigator.mediaDevices.getUserMedia({
											video: { width: 640, height: 360 },
											audio: false,
										}),
									);
								})
							}
						>
							<Video size={16} />
							{preview ? "Turn preview off" : "Preview camera"}
						</button>
					</div>
					<div className="feature-row">
						<div>
							<Users size={19} />
							<strong>Small by design</strong>
							<p>Six seats. Space for everyone.</p>
						</div>
						<div>
							<Link size={19} />
							<strong>Just share a link</strong>
							<p>Your guests bring their wallet.</p>
						</div>
					</div>
					<div className="office-note">
						<AudioLines size={22} />
						<div>
							<strong>The conversation stays with your company.</strong>
							<p>
								When Office is online, audio is captured automatically. Find
								recordings and transcripts in Office.
							</p>
						</div>
					</div>
				</section>
			</main>
			<footer>
				<span>Built for the way pioneers work.</span>
				<span>VIDEO · VOICE · IDEAS</span>
			</footer>
		</div>
	);
}
